import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ArrowDown, ArrowRight, Bot, CheckCircle2, ChevronDown, ChevronLeft, ChevronRight, Clock3, Gift, Headphones, Heart, HeartHandshake, LifeBuoy, Mail, Music4, Pause, Play, Send, ShieldCheck, Sparkles, Star, X } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { api } from "@/lib/api";
import { initPixel, initTikTokPixel } from "@/lib/pixel";
import CreateSongCta, { openWizard } from "@/components/SongWizard";
import { PremiumModal } from "@/components/app/PremiumCard";
import ReviewsRibbon from "@/components/ReviewsRibbon";
import LastOrderWidget from "@/components/LastOrderWidget";

const LogoBars = ({ size = 30 }) => (
  <svg width={size} height={size} viewBox="0 0 40 40" fill="none" aria-hidden>
    <rect x="2" y="14" width="5" height="12" rx="2.5" fill="#2BA8FF" />
    <rect x="10" y="6" width="5" height="28" rx="2.5" fill="#2BA8FF" />
    <rect x="18" y="2" width="5" height="36" rx="2.5" fill="#FFD400" />
    <rect x="26" y="9" width="5" height="22" rx="2.5" fill="#2BA8FF" />
    <rect x="34" y="15" width="4" height="10" rx="2" fill="#FFD400" />
  </svg>
);

const PILL_ICONS = ["/icons/wand.png", "/icons/palette.png", "/icons/gift.png", "/icons/notes.png"];
const STEP_ICONS = [Sparkles, Music4, Play, Star, Heart];
const QUALITY_ICONS = { regen: ShieldCheck, bot: Bot, support: Headphones, person: HeartHandshake };

const ACCENTS = [
  {
    border: "rgba(21,159,245,.65)",
    priceBg: "linear-gradient(135deg,#2BA8FF,#1E86E8)", priceColor: "#ffffff",
    label: "#23A9FF", labelBorder: "rgba(35,169,255,.25)", labelBg: "rgba(35,169,255,.10)",
    glow: "0 0 55px rgba(20,155,245,.22)", glowColor: "rgba(35,169,255,.7)",
    bgImage: "linear-gradient(115deg, rgba(43,168,255,0.04) 0%, rgba(43,168,255,0.14) 55%, rgba(43,168,255,0.28) 100%)",
    img: "/images/plan-bot.png",
  },
  {
    border: "rgba(255,205,30,.5)",
    priceBg: "linear-gradient(135deg,#FFD400,#FFB300)", priceColor: "#060A14",
    label: "#FFD21A", labelBorder: "rgba(255,205,30,.25)", labelBg: "rgba(255,205,30,.10)",
    glow: "0 0 35px rgba(255,205,20,.10)", glowColor: "rgba(255,205,20,.55)",
    bgImage: "linear-gradient(115deg, rgba(255,205,30,0.02) 0%, rgba(255,205,30,0.08) 55%, rgba(255,205,30,0.18) 100%)",
    img: "/images/plan-team.png",
  },
  {
    border: "rgba(155,77,255,.65)",
    priceBg: "linear-gradient(135deg,#A855F7,#7C3AED)", priceColor: "#ffffff",
    label: "#B98BFF", labelBorder: "rgba(155,77,255,.30)", labelBg: "rgba(155,77,255,.12)",
    glow: "0 0 30px rgba(155,77,255,.10)", glowColor: "rgba(155,77,255,.55)",
    bgImage: "linear-gradient(115deg, rgba(155,77,255,0.02) 0%, rgba(155,77,255,0.09) 55%, rgba(155,77,255,0.20) 100%)",
    img: "/images/plan-site-purple.png",
  },
];

const EN_SITE_ACCENT = {
  ...ACCENTS[2],
  priceBg: ACCENTS[1].priceBg,
  priceColor: ACCENTS[1].priceColor,
  img: ACCENTS[1].img,
  imgFilter: "none",
};

function PlanCard({ plan, accent, onClick, testid, shakeClass }) {
  return (
    <button data-testid={testid} onClick={onClick}
      className={`relative w-full text-left rounded-[22px] lg:rounded-[28px] border p-3.5 pl-4 pt-5 pb-9 lg:p-4 lg:pl-5 lg:pt-6 lg:pb-12 group outline-none focus:outline-none flex flex-col min-h-[148px] lg:min-h-[225px] ${shakeClass ? `tariff-shake ${shakeClass}` : ""}`}
      style={{
        backgroundColor: "#07182C",
        backgroundImage: accent.bgImage,
        borderColor: accent.border,
        boxShadow: `${accent.glow}, inset 0 1px 0 rgba(255,255,255,0.04)`,
      }}>
      {/* цена — как в конструкторе: наклонный бейдж на верхней обводке справа */}
      <span className="absolute -top-3 right-4 lg:right-5 rotate-[8deg] rounded-xl px-2.5 lg:px-3.5 py-0.5 lg:py-1.5 text-[13px] lg:text-base font-display font-extrabold shadow-xl"
        style={{ background: accent.priceBg, color: accent.priceColor }}>
        {plan.price}
      </span>
      {/* картинка справа */}
      <span className="pointer-events-none absolute right-0 top-2 bottom-0 w-[38%] lg:w-[42%] flex items-end justify-end">
        <img src={accent.img} alt="" draggable={false}
          className="h-full w-full object-contain object-right select-none"
          style={{ filter: accent.imgFilter || `drop-shadow(0 0 18px ${accent.glowColor})` }} />
      </span>

      <h3 className="font-display font-extrabold uppercase text-[15px] lg:text-[20px] leading-[1.05] pr-[42%] lg:pr-[40%]">
        {plan.title}
      </h3>
      <p className="text-[11.5px] lg:text-[13px] font-medium leading-snug mt-1 lg:mt-1.5 pr-[42%] lg:pr-[40%]" style={{ color: "#9BAFC8" }}>
        {plan.desc}
      </p>
      {plan.feats?.length ? (
        <ul className="mt-2 space-y-1 pr-[42%] lg:pr-[40%]">
          {plan.feats.map((f) => (
            <li key={f} className="flex items-start gap-1.5 text-[10.5px] lg:text-[12px] font-semibold leading-snug"
              style={{ color: accent.label }}>
              <span className="mt-[5px] w-1 h-1 rounded-full shrink-0" style={{ background: accent.label }} />
              {f}
            </li>
          ))}
        </ul>
      ) : null}
      {/* кнопка «Натисни» — прямоугольник с округлыми краями */}
      <span className="absolute right-2.5 bottom-2.5 lg:right-3 lg:bottom-3 z-10 inline-flex items-center gap-1.5 rounded-xl border border-white/25 bg-white/10 backdrop-blur-md px-3 py-1.5 lg:px-3.5 lg:py-2 text-[11px] lg:text-xs font-bold transition-transform group-hover:translate-x-1 group-active:scale-95"
        style={{ color: accent.label, boxShadow: accent.glow }}>
        {plan.cta} <ArrowRight size={14} />
      </span>
      {/* маленькая пометка времени с кругом */}
      <span className="absolute left-4 bottom-3 lg:left-5 lg:bottom-4 z-10 inline-flex items-center gap-1.5 text-[10px] lg:text-[11px] font-mono uppercase tracking-wider"
        style={{ color: "#8FA0BA" }}>
        <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: accent.label }} />
        {plan.eta}
      </span>
    </button>
  );
}

function fmtTime(s) {
  if (!s || !isFinite(s)) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${String(sec).padStart(2, "0")}`;
}

const ORDER_GROUP_STYLE = {
  unpaid: { color: "#FFD34D", border: "rgba(255,211,77,.35)", bg: "rgba(255,211,77,.10)" },
  review: { color: "#5AA9FF", border: "rgba(90,169,255,.35)", bg: "rgba(47,128,255,.10)" },
  generating: { color: "#29B6FF", border: "rgba(41,182,255,.35)", bg: "rgba(41,182,255,.10)" },
  done: { color: "#4ADE80", border: "rgba(74,222,128,.35)", bg: "rgba(74,222,128,.10)" },
  rejected: { color: "#FF5C67", border: "rgba(255,92,103,.35)", bg: "rgba(255,92,103,.10)" },
  expired: { color: "#7F8BA0", border: "rgba(255,255,255,.12)", bg: "rgba(255,255,255,.05)" },
};

function MyOrdersModal({ open, onClose }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const load = useCallback(() => {
    let cid = "";
    try { cid = localStorage.getItem("episnya_client_id") || ""; } catch { /* ignore */ }
    if (!cid) return;
    setLoading(true);
    api.get("/public/landing/state", { params: { client_id: cid, done_first: 1, page: 1, per_page: 10 } })
      .then((r) => setItems(r.data?.items || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => {
    if (!open) return undefined;
    load();
    const id = setInterval(load, 10000);
    return () => clearInterval(id);
  }, [open, load]);
  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-[120] bg-[#060A14]/80 backdrop-blur-sm overflow-y-auto"
      role="dialog" aria-modal="true" data-testid="myorders-modal" onClick={onClose}>
      <div className="max-w-xl mx-auto px-4 py-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between gap-4 mb-4">
          <h3 className="font-display text-xl font-extrabold uppercase">Мої замовлення</h3>
          <button onClick={onClose} aria-label="Закрити"
            className="grid place-items-center w-9 h-9 rounded-full border border-white/15 text-[#9FB0C7] hover:text-white hover:border-white/30 transition-colors">
            <X size={17} />
          </button>
        </div>
        {items.length === 0 ? (
          <div className="rounded-3xl border border-white/10 bg-white/[0.05] backdrop-blur-xl p-8 text-center text-[#9FB0C7]">
            {loading ? "Завантажую…" : "Замовлень поки немає"}
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((it) => {
              const st = ORDER_GROUP_STYLE[it.status_group] || ORDER_GROUP_STYLE.review;
              const url = it.listen_url
                ? (it.listen_url.startsWith("http") ? it.listen_url : `${window.location.origin}${it.listen_url}`)
                : "";
              return (
                <div key={it.id} className="rounded-3xl border border-white/12 bg-white/[0.06] backdrop-blur-xl p-4">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[11px] font-bold px-2.5 py-0.5 rounded-full border"
                      style={{ color: st.color, borderColor: st.border, background: st.bg }}>
                      {it.status_label || it.status}
                    </span>
                    {it.number && <span className="font-mono text-xs text-[#C6D3E6]">#{it.number}</span>}
                    <span className="text-[11px] text-[#647089] font-mono ml-auto">
                      {(it.created_at || "").slice(0, 10)}
                    </span>
                  </div>
                  <p className="mt-2 text-sm text-[#C6D3E6]">
                    {it.song_title || ((it.genre_mix || []).length ? "Мікс" : it.genre_label) || "Пісня"}
                    {it.song_title && (((it.genre_mix || []).length ? "Мікс" : it.genre_label))
                      ? ` · ${(it.genre_mix || []).length ? "Мікс" : it.genre_label}` : ""}
                  </p>
                  {it.description && (
                    <p className="mt-1 text-xs text-[#8CA0BC] line-clamp-2">{it.description}</p>
                  )}
                  {url && (
                    <a href={url} target="_blank" rel="noopener noreferrer"
                      className="mt-3 inline-flex items-center gap-2 rounded-xl border border-[#10B981]/40 bg-[#10B981]/10 text-[#4ADE80] text-xs font-bold px-4 py-2 hover:bg-[#10B981]/20 transition-colors">
                      🎧 Відкрити замовлення
                    </a>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

function SupportModal({ open, onClose }) {
  const { t } = useLang();
  const [form, setForm] = useState({ email: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open) return;
    setForm({ email: "", message: "" });
    setSent(false);
    setErr("");
    setBusy(false);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  const submit = async () => {
    if (busy) return;
    const email = form.email.trim();
    const message = form.message.trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) { setErr(t.support_err_email); return; }
    if (message.length < 10) { setErr(t.support_err_text); return; }
    setBusy(true);
    setErr("");
    let clientId = "";
    try { clientId = localStorage.getItem("episnya_client_id") || ""; } catch { /* ignore */ }
    try {
      await api.post("/public/support", {
        email,
        message,
        lang: "en",
        page: window.location.href,
        client_id: clientId || undefined,
      });
      setSent(true);
    } catch {
      setErr(t.support_err_send);
    } finally {
      setBusy(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[140] flex items-center justify-center bg-[#060A14]/85 backdrop-blur-md px-4 py-6 overflow-y-auto"
      onClick={onClose} data-testid="support-modal" role="dialog" aria-modal="true">
      <div className="relative w-full max-w-md overflow-hidden rounded-[30px] border border-white/10 bg-[#0B1424] p-6 sm:p-7 my-auto"
        style={{ boxShadow: "0 28px 90px rgba(0,0,0,0.65), inset 0 1px 0 rgba(255,255,255,0.06)" }}
        onClick={(e) => e.stopPropagation()}>
        <div className="pointer-events-none absolute -left-24 -top-28 h-64 w-64 rounded-full bg-[#22C55E]/20 blur-3xl" />
        <div className="pointer-events-none absolute -right-24 -top-20 h-56 w-56 rounded-full bg-[#2BA8FF]/15 blur-3xl" />

        <button type="button" onClick={onClose} aria-label={t.support_close}
          className="absolute right-4 top-4 z-10 grid h-9 w-9 place-items-center rounded-full border border-white/10 bg-white/5 text-[#9FB0C7] transition-colors hover:border-white/30 hover:text-white">
          <X size={17} />
        </button>

        <div className="relative">
          <div className="flex items-start gap-3.5 pr-10">
            <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-gradient-to-br from-[#22C55E] to-[#10B981] shadow-[0_14px_34px_-12px_rgba(34,197,94,0.9)]">
              <LifeBuoy size={23} className="text-white" />
            </span>
            <div className="min-w-0 pt-0.5">
              <h3 className="font-display text-xl font-extrabold leading-tight">{t.support_modal_title}</h3>
              <p className="mt-1 text-xs leading-relaxed text-[#9FB0C7]">{t.support_modal_sub}</p>
            </div>
          </div>

          {sent ? (
            <div className="py-8 text-center">
              <div className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-gradient-to-br from-[#22C55E] to-[#10B981] shadow-[0_18px_44px_-16px_rgba(34,197,94,0.95)]">
                <CheckCircle2 size={30} className="text-white" />
              </div>
              <p className="mt-5 font-display text-lg font-extrabold">{t.support_sent_title}</p>
              <p className="mx-auto mt-2 max-w-xs text-sm leading-relaxed text-[#9FB0C7]">{t.support_sent_text}</p>
              <button type="button" onClick={onClose}
                className="btn-instr mt-6 w-full rounded-2xl px-6 py-3 text-sm font-bold text-white">
                {t.support_close}
              </button>
            </div>
          ) : (
            <div className="mt-6 space-y-4">
              <div>
                <label htmlFor="support-email" className="text-[11px] font-mono uppercase tracking-wider text-[#8FA0BA]">{t.support_email}</label>
                <div className="relative mt-2">
                  <Mail size={16} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-[#647089]" />
                  <input id="support-email" data-testid="support-email" type="email" inputMode="email"
                    autoComplete="email" value={form.email} maxLength={120}
                    onChange={(e) => { setForm((f) => ({ ...f, email: e.target.value })); setErr(""); }}
                    placeholder={t.support_email_ph}
                    className="w-full rounded-2xl border border-white/10 bg-[#08111F] py-3 pl-11 pr-4 text-base text-white outline-none transition-colors placeholder:text-[#52627A] focus:border-[#22C55E]/70" />
                </div>
              </div>

              <div>
                <label htmlFor="support-message" className="text-[11px] font-mono uppercase tracking-wider text-[#8FA0BA]">{t.support_question}</label>
                <textarea id="support-message" data-testid="support-message" rows={5} maxLength={2000}
                  value={form.message}
                  onChange={(e) => { setForm((f) => ({ ...f, message: e.target.value })); setErr(""); }}
                  placeholder={t.support_question_ph}
                  className="mt-2 w-full resize-none rounded-2xl border border-white/10 bg-[#08111F] p-4 text-base leading-relaxed text-white outline-none transition-colors placeholder:text-[#52627A] focus:border-[#22C55E]/70" />
              </div>

              {err && <p className="text-sm text-[#FF8A8A]">{err}</p>}

              <button type="button" data-testid="support-submit" onClick={submit} disabled={busy}
                className="btn-instr flex w-full items-center justify-center gap-2 rounded-2xl px-6 py-3.5 text-sm font-bold text-white disabled:cursor-wait disabled:opacity-60">
                <Send size={16} /> {busy ? t.support_sending : t.support_send}
              </button>

              <p className="flex items-center justify-center gap-1.5 text-center text-[11px] leading-relaxed text-[#647089]">
                <Clock3 size={13} className="shrink-0 text-[#22C55E]" /> {t.support_note}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default function Landing() {
  const { lang, setLang, t, enSite } = useLang();
  const [blocked, setBlocked] = useState(false);
  const [premiumOpen, setPremiumOpen] = useState(false);
  const [giftOpen, setGiftOpen] = useState(false);
  const [instrOpen, setInstrOpen] = useState(false);
  const [supportOpen, setSupportOpen] = useState(false);
  const [myOrdersOpen, setMyOrdersOpen] = useState(false);
  const [botLink, setBotLink] = useState("https://t.me/episnyabot");
  const [fbCode, setFbCode] = useState(null);
  const [ttCode, setTtCode] = useState(null);
  const [refCode, setRefCode] = useState("");
  const [stats, setStats] = useState({ songs_generated: 1500, rating: 5, satisfaction: 100, avg_minutes: 2 });
  const [openFaq, setOpenFaq] = useState(0);
  const webCtx = useRef({});
  const exRefs = useRef([]);
  const exTouch = useRef(null);
  const exSwipeAt = useRef(0);
  const [exIdx, setExIdx] = useState(0);
  const [exPlaying, setExPlaying] = useState(null);
  const [exTime, setExTime] = useState(0);
  const [exDur, setExDur] = useState([]);
  const EX_N = t.examples.length;

  useEffect(() => {
    document.title = enSite
      ? "E-PISNYA — Custom songs for any occasion"
      : "E-PISNYA — створи власну пісню";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute("content", enSite
        ? "Turn your story into a personal song for any occasion. Lyrics, vocals and music in minutes — $29.99."
        : "E-PISNYA — ШІ створить персональну пісню на будь-який привід: день народження, весілля, ювілей чи просто так. Текст, вокал і музика за пару хвилин.");
    }
  }, [enSite]);

  const checkBlock = useCallback(() => {
    let cid = "";
    try { cid = localStorage.getItem("episnya_client_id") || ""; } catch { /* ignore */ }
    api.get("/public/block-check", { params: { client_id: cid } })
      .then((r) => { if (r.data?.blocked) setBlocked(true); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    checkBlock();
    const id = setInterval(checkBlock, 10000);
    const onVis = () => { if (document.visibilityState === "visible") checkBlock(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { clearInterval(id); document.removeEventListener("visibilitychange", onVis); };
  }, [checkBlock]);

  // Вход на лендинг: карточка в чат входов (client_id + IP + блок-кнопки).
  useEffect(() => {
    let cid = "";
    let seen = true;
    try {
      cid = localStorage.getItem("episnya_client_id") || "";
      seen = !!sessionStorage.getItem("episnya_site_enter");
    } catch { /* ignore */ }
    if (!cid || seen) return;
    try { sessionStorage.setItem("episnya_site_enter", "1"); } catch { /* ignore */ }
    const ref = new URLSearchParams(window.location.search).get("ref") || "";
    api.post("/public/landing/enter", { client_id: cid, ref }).catch(() => {});
  }, []);

  // Подарочная модалка — один раз при первом входе (только UA-версия).
  useEffect(() => {
    if (enSite) return undefined;
    let seen = true;
    try { seen = !!localStorage.getItem("episnya_gift_seen"); } catch { /* ignore */ }
    if (seen) return undefined;
    const id = setTimeout(() => setGiftOpen(true), 900);
    return () => clearTimeout(id);
  }, [enSite]);

  useEffect(() => {
    const open = () => setMyOrdersOpen(true);
    window.addEventListener("episnya:open-myorders", open);
    return () => window.removeEventListener("episnya:open-myorders", open);
  }, []);

  const closeGift = () => {
    try { localStorage.setItem("episnya_gift_seen", "1"); } catch { /* ignore */ }
    setGiftOpen(false);
  };

  useEffect(() => {
    if ("scrollRestoration" in window.history) window.history.scrollRestoration = "manual";
    if (window.location.hash) window.history.replaceState(null, "", window.location.pathname);
    window.scrollTo(0, 0);
    api.get("/public/stats").then((r) => {
      if (r.data.bot_link) setBotLink(r.data.bot_link);
      if (r.data) setStats((s) => ({ ...s, ...r.data }));
    }).catch(() => {});
  }, []);

  // Meta Pixel + Facebook-клик: ловим fbclid, получаем короткий код для ссылки в бота
  useEffect(() => {
    initPixel();
    initTikTokPixel();
    // Воронка TikTok: нужен ViewContent с content_id (диагностика пикселя).
    try {
      if (window.ttq) {
        window.ttq.track("ViewContent", {
          content_id: "episnya_song", content_type: "product",
          value: enSite ? 29.99 : 169, currency: enSite ? "USD" : "UAH",
        });
      }
    } catch { /* ignore */ }
    const params = new URLSearchParams(window.location.search);
    const fbclid = params.get("fbclid") || sessionStorage.getItem("fb_clid") || "";
    if (fbclid) sessionStorage.setItem("fb_clid", fbclid);
    const saved = sessionStorage.getItem("fb_code");
    if (saved) setFbCode(saved);
    // TikTok-клик: ttclid → код для ссылки в бота (аналог fbclid).
    const ttclid = params.get("ttclid") || sessionStorage.getItem("tt_clid") || "";
    if (ttclid) sessionStorage.setItem("tt_clid", ttclid);
    const savedTt = sessionStorage.getItem("tt_code");
    if (savedTt) setTtCode(savedTt);
    // Індивідуальна лінка на сайт: ?ref=sl_<код> — код переїде в бота (?start=sl_<код>)
    const rawRef = params.get("ref") || params.get("sl") || "";
    let ref = rawRef ? (rawRef.startsWith("sl_") ? rawRef : `sl_${rawRef}`) : "";
    ref = ref || sessionStorage.getItem("ref_code") || "";
    if (ref) sessionStorage.setItem("ref_code", ref);
    setRefCode(ref);
    // Короткий параметр ?g=<группа> — читаемая метка группы (идёт отдельно от utm-меток)
    const shortGroup = params.get("g") || "";
    const ctx = {
      fbclid,
      lang: enSite ? "en" : "ua",
      group: shortGroup || sessionStorage.getItem("group") || "",
      utm_source: params.get("utm_source") || sessionStorage.getItem("utm_source") || "",
      utm_campaign: params.get("utm_campaign") || sessionStorage.getItem("utm_campaign") || "",
      utm_term: params.get("utm_term") || sessionStorage.getItem("utm_term") || "",
      utm_content: params.get("utm_content") || sessionStorage.getItem("utm_content") || "",
    };
    ["group", "utm_source", "utm_campaign", "utm_term", "utm_content"].forEach((k) => {
      if (ctx[k]) sessionStorage.setItem(k, ctx[k]);
    });
    let cid = "";
    try { cid = localStorage.getItem("episnya_client_id") || ""; } catch { /* ignore */ }
    if (!cid) {
      cid = window.crypto?.randomUUID?.()
        || `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      try { localStorage.setItem("episnya_client_id", cid); } catch { /* ignore */ }
    }
    ctx.client_id = cid;
    webCtx.current = ctx;
    api.post("/web/visit", ref ? { ...ctx, ref } : ctx).catch(() => {});
    // Клік по партнерській лінці: один раз за сесію (як /fb/hit для fbclid).
    let refHit = true;
    try { refHit = !!sessionStorage.getItem("ref_hit"); } catch { /* ignore */ }
    if (ref && cid && !refHit) {
      try { sessionStorage.setItem("ref_hit", "1"); } catch { /* ignore */ }
      api.post("/ref/hit", { ref, client_id: cid }).catch(() => {});
    }
    if (ttclid && !savedTt) {
      api.post("/tt/hit", {
        ttclid,
        source: "landing",
        group: ctx.group,
        utm_source: ctx.utm_source,
        utm_campaign: ctx.utm_campaign,
        utm_term: ctx.utm_term,
        utm_content: ctx.utm_content,
      }).then((r) => {
        if (r.data?.code) {
          sessionStorage.setItem("tt_code", r.data.code);
          setTtCode(r.data.code);
        }
      }).catch(() => {});
    }
    if (!fbclid || saved) return;
    api.post("/fb/hit", {
      fbclid,
      source: "landing",
      group: ctx.group,
      utm_source: ctx.utm_source,
      utm_campaign: ctx.utm_campaign,
      utm_term: ctx.utm_term,
      utm_content: ctx.utm_content,
    }).then((r) => {
      if (r.data?.code) {
        sessionStorage.setItem("fb_code", r.data.code);
        setFbCode(r.data.code);
      }
    }).catch(() => {});
  }, [enSite]);

  const track = (source) => {
    api.post("/web/click", { ...webCtx.current, source }).catch(() => {});
  };

  const goBot = (source = "site") => {
    const src = typeof source === "string" ? source : "site";
    track(src);
    const url = fbCode ? `${botLink}?start=fb_${fbCode}_${src}`
      : ttCode ? `${botLink}?start=tt_${ttCode}_${src}`
        : (refCode ? `${botLink}?start=${refCode}` : botLink);
    window.open(url, "_blank");
  };

  // Тариф «Під ключ» (студия) и «Створи на сайті» (флоу як у боті) — оба на сайте.
  const openSite = (source, mode = "bot") => {
    track(source);
    // Воронка TikTok: открыл конструктор = AddToCart (выбрал товар).
    try {
      if (window.ttq) {
        window.ttq.track("AddToCart", {
          content_id: "episnya_song", content_type: "product",
          value: enSite ? 29.99 : 169, currency: enSite ? "USD" : "UAH",
        });
      }
    } catch { /* ignore */ }
    openWizard(mode);
  };

  const plans = [
    { title: t.plan_bot_title, price: t.plan_bot_price, desc: t.plan_bot_desc, cta: t.plan_click, eta: t.plan_eta_bot,
      feats: ["контроль за текстом", "можлива перегенерація", "Відео з фото під музику"] },
    { title: t.plan_site_title, price: t.plan_site_price, desc: t.plan_site_desc, cta: t.plan_click, eta: t.plan_eta_site,
      feats: enSite
        ? ["Your story will turn into a song — no skills needed, just a few taps.",
           "Free regeneration", "Song delivered by email or messenger"]
        : ["Доступна перегенерація", "Отримаєш пісню на сайті"] },
    { title: t.plan_team_title, price: t.plan_team_price, desc: t.plan_team_desc, cta: t.plan_click, eta: t.plan_eta_team },
  ];

  const toggleEx = (i) => {
    exRefs.current.forEach((el, j) => { if (el && j !== i && !el.paused) el.pause(); });
    const el = exRefs.current[i];
    if (!el) return;
    if (el.paused) {
      el.play(); setExPlaying(i); setExTime(el.currentTime);
      api.post("/web/play", { ...webCtx.current, source: `example_${i + 1}` }).catch(() => {});
    } else { el.pause(); setExPlaying(null); }
  };

  const seekEx = (i, e) => {
    const el = exRefs.current[i];
    const dur = exDur[i] || 0;
    if (!el || !dur) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const t2 = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width)) * dur;
    el.currentTime = t2;
    setExTime(t2);
  };

  const renderExample = (ex, i) => {
    const active = exPlaying === i;
    const dur = exDur[i] || 0;
    const cur = active ? exTime : 0;
    const meta = [ex.mood, ex.voice].filter(Boolean);
    return (
      <article key={i} data-testid={`example-card-${i}`} className="w-full shrink-0 px-1 h-full">
        <div className="relative h-full flex flex-col overflow-hidden rounded-[28px] border border-white/12 bg-[linear-gradient(180deg,rgba(17,29,49,.94),rgba(7,13,25,.99))] shadow-[0_26px_80px_-34px_rgba(0,0,0,.95)]">
          <div className="relative aspect-[3/2] overflow-hidden" style={ex.artBg ? { background: ex.artBg } : undefined}>
            {ex.art ? (
              <>
                <div className="absolute left-1/2 top-1/2 h-[72%] w-[72%] -translate-x-1/2 -translate-y-1/2 rounded-full blur-3xl"
                  style={{ background: ex.artGlow || "rgba(43,168,255,.25)" }} />
                <img src={ex.art} alt="" loading="lazy"
                  className="absolute inset-0 m-auto h-[76%] w-[76%] object-contain drop-shadow-[0_18px_30px_rgba(0,0,0,0.45)]" />
              </>
            ) : (
              <img src={ex.img} alt={ex.title} loading="lazy"
                className={`w-full h-full object-cover ${ex.pos || ""}`} />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-[#060A14]/80 via-transparent to-transparent" />
            {(ex.badge || ex.order) && (
              <div className="absolute inset-x-3 top-3 flex items-center justify-between gap-2">
                {ex.badge ? (
                  <span className="rounded-full border border-white/15 bg-black/25 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-white/90 backdrop-blur-md">{ex.badge}</span>
                ) : <span />}
                {ex.order ? (
                  <span className="rounded-full border border-white/15 bg-black/25 px-2 py-1 font-mono text-[10px] text-white/75 backdrop-blur-md">{ex.order}</span>
                ) : null}
              </div>
            )}
            {ex.review ? (
              <div className="absolute inset-x-2.5 bottom-2.5">
                <div className="rounded-2xl border border-white/15 bg-white/[0.07] backdrop-blur-md px-3 py-2">
                  <p className="text-white/90 leading-snug text-[10px]">
                    <span className="text-[#2BA8FF] font-bold align-[-2px] text-sm">“</span>
                    {ex.review}
                  </p>
                </div>
              </div>
            ) : null}
          </div>
          <div className="p-5 flex flex-col flex-1">
            <h3 className="font-display font-bold leading-tight text-[15px]">{ex.title}</h3>
            <span className="text-xs font-mono text-[#2BA8FF] mt-1">{ex.tag}</span>
            {meta.length ? (
              <div className="mt-3 flex flex-wrap gap-1.5">
                {meta.map((m) => (
                  <span key={m} className="rounded-full border border-white/10 bg-white/[0.04] px-2 py-1 text-[10px] font-mono text-[#9FB0C7]">{m}</span>
                ))}
              </div>
            ) : null}
            <p className="text-[#9FB0C7] mt-2 leading-relaxed flex-1 text-xs">{ex.desc}</p>
            <div className="mt-4 flex items-center gap-3">
              <button data-testid={`audio-player-play-button-${i}`} onClick={() => toggleEx(i)}
                aria-label={ex.title}
                className="grid place-items-center rounded-full grad-tg shrink-0 hover:opacity-90 transition-opacity w-10 h-10">
                {active ? <Pause size={15} className="text-white" />
                        : <Play size={15} className="text-white ml-0.5" />}
              </button>
              <div className="flex-1 min-w-0">
                <div className="h-1.5 rounded-full bg-white/10 overflow-hidden cursor-pointer"
                     onClick={(e) => seekEx(i, e)}>
                  <div className="h-full bg-[#2BA8FF] transition-[width] duration-200"
                       style={{ width: `${dur ? (cur / dur) * 100 : 0}%` }} />
                </div>
                <div className="flex justify-between text-[11px] font-mono text-[#647089] mt-1.5 tabular-nums">
                  <span>{fmtTime(cur)}</span><span>{fmtTime(dur)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </article>
    );
  };

  const nf = (n) => (n || 0).toLocaleString("uk-UA").replace(/,/g, " ");

  const tariffsRef = useRef(null);
  const scrollToTariffs = () => {
    track("nav_tariffs");
    const el = tariffsRef.current;
    if (!el) return;
    const y = el.getBoundingClientRect().top + window.scrollY - 72;
    window.scrollTo({ top: Math.max(0, y), behavior: "smooth" });
  };

  if (blocked) {
    return (
      <div className="fixed inset-0 bg-[#030914] grid place-items-center" data-testid="blocked-screen">
        <div className="flex flex-col items-center gap-5">
          <div className="text-6xl select-none">🔒</div>
          {!enSite ? (
            <>
              <a href="https://t.me/episnyasupport" target="_blank" rel="noopener noreferrer"
                className="rounded-2xl px-6 py-3 font-bold text-sm text-white"
                style={{ background: "linear-gradient(135deg,#2BA8FF,#1E86E8)", boxShadow: "0 10px 30px rgba(43,168,255,0.35)" }}>
                💬 Написати в підтримку
              </a>
              <div className="text-sm text-[#9FB0C7]">Support: @episnyasupport</div>
            </>
          ) : (
            <div className="text-sm text-[#9FB0C7]">Access from this device is restricted.</div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen text-[#F4F7FC] overflow-x-hidden"
      style={{ background: "linear-gradient(180deg,#071120 0%,#030914 45%,#020711 100%)" }}>
      {/* HEADER */}
      <header className="sticky top-0 inset-x-0 z-50 glass" data-testid="landing-navbar">
        <div className="max-w-xl lg:max-w-5xl mx-auto px-5 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <LogoBars size={28} />
            <span className="font-display font-extrabold text-xl tracking-tight">E-PISNYA</span>
          </div>
          <div className="flex items-center gap-3">
            {enSite ? (
              <button data-testid="nav-support-button"
                onClick={() => { track("nav_support"); setSupportOpen(true); }}
                className="btn-instr text-white font-bold text-sm px-4 py-2.5 rounded-full flex items-center gap-1.5 transition-transform hover:scale-105 whitespace-nowrap">
                <LifeBuoy size={15} /> {t.nav_support || "Support"}
              </button>
            ) : (
              <button data-testid="nav-instruction-button" onClick={() => setInstrOpen(true)}
                className="btn-instr text-white font-bold text-sm px-4 py-2.5 rounded-full flex items-center gap-1.5 transition-transform hover:scale-105 whitespace-nowrap">
                <Play size={15} fill="currentColor" /> {t.nav_instruction || "Інструкція"}
              </button>
            )}
          </div>
        </div>
      </header>

      {/* HERO — только типографика */}
      <section className="relative px-5 pt-7 sm:pt-14 pb-2">
        <div className="absolute inset-0 hero-flag pointer-events-none opacity-60" />
        <div className="relative max-w-xl lg:max-w-5xl mx-auto">
          <h1 className="sr-only">{t.hero_title_1} {t.hero_title_hl} — E-PISNYA</h1>
          <h2 className="font-display font-extrabold uppercase leading-[1.05] tracking-[-0.8px] text-[28px] sm:text-[32px]">
            {t.hero_headline_1}<br />
            <span className="text-yellow">
              {t.hero_line2}
              {t.hero_line3 ? <><br />{t.hero_line3}</> : null}
            </span>
          </h2>
          <p className="hand text-[22px] sm:text-[26px] mt-4 leading-none" style={{ color: "#20A9FF" }}>
            {t.tagline}
          </p>
        </div>
      </section>

      {/* PRICING — главный блок */}
      <section className="px-5 pt-6 sm:pt-10 pb-10">
        <div className="max-w-xl lg:max-w-5xl mx-auto">
          <div className="tariff-head" ref={tariffsRef}>
            <h2 className="font-display text-[20px] sm:text-[22px] font-extrabold uppercase" style={{ color: "#F5F7FA" }}>
              {t.tariffs_title}
            </h2>
          </div>
          {/* Указатель на тарифы: стрелка прямо над карточками */}
          <div className="tariff-point" aria-hidden>
            <span className="tariff-arrow tariff-arrow-lg">
              <ArrowDown size={26} strokeWidth={3.2} />
            </span>
          </div>
          <div className={`mt-4 sm:mt-5 grid gap-3.5 ${enSite ? "" : "lg:grid-cols-2"}`}>
            {!enSite && (
              <PlanCard plan={plans[0]} accent={ACCENTS[0]} testid="tariff-bot" shakeClass="tariff-shake-1"
                onClick={() => goBot("tariff_bot")} />
            )}
            <PlanCard plan={plans[1]} accent={enSite ? EN_SITE_ACCENT : ACCENTS[2]} testid="tariff-site" shakeClass="tariff-shake-2"
              onClick={() => openSite("tariff_site")} />
          </div>
        </div>
      </section>

      {/* ВІДГУКИ — ниже тарифов */}
      <section className="px-5 pt-2 pb-4">
        <div className="max-w-xl lg:max-w-5xl mx-auto">
          <ReviewsRibbon />
        </div>
      </section>

      {/* НАШІ ПІСНІ — свайп-карусель как на проде */}
      <section className="px-5 pt-6 pb-12">
        <div className="max-w-xl lg:max-w-3xl mx-auto">
          <div className="flex items-center justify-between gap-3 mb-4">
            <div className="flex items-center gap-3 min-w-0">
              <span className="grid place-items-center w-10 h-10 rounded-2xl grad-tg shrink-0"
                style={{ boxShadow: "0 8px 26px rgba(43,168,255,0.4)" }}>
                <Music4 size={18} className="text-white" />
              </span>
              <div className="min-w-0">
                <p className="font-display font-extrabold uppercase text-sm sm:text-base leading-none">{t.ex_title}</p>
                <p className="text-[11px] text-[#647089] font-mono mt-1 truncate">{t.ex_sub}</p>
              </div>
            </div>
            <span className="font-mono text-xs text-[#647089] tabular-nums shrink-0">{exIdx + 1}/{EX_N}</span>
          </div>

          <div className="relative overflow-hidden touch-pan-y rounded-3xl"
            onTouchStart={(e) => {
              const t0 = e.touches[0];
              if (!t0) return;
              exTouch.current = { x: t0.clientX, y: t0.clientY };
            }}
            onTouchCancel={() => { exTouch.current = null; }}
            onTouchEnd={(e) => {
              const start = exTouch.current;
              exTouch.current = null;
              if (!start) return;
              const t1 = e.changedTouches[0];
              if (!t1) return;
              const dx = t1.clientX - start.x;
              const dy = t1.clientY - start.y;
              if (Math.abs(dx) < 60 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
              const now = Date.now();
              if (now - exSwipeAt.current < 450) return;
              exSwipeAt.current = now;
              setExIdx((idx) => (dx < 0 ? (idx + 1) % EX_N : (idx - 1 + EX_N) % EX_N));
            }}>
            <div className="flex transition-transform duration-500 ease-out"
              style={{ transform: `translateX(-${exIdx * 100}%)` }}>
              {t.examples.map((ex, i) => renderExample(ex, i))}
            </div>
          </div>

          <div className="mt-4 flex items-center justify-center gap-5">
            <button data-testid="examples-prev" onClick={() => setExIdx((exIdx - 1 + EX_N) % EX_N)}
              className="grid place-items-center w-11 h-11 rounded-full border border-white/12 bg-white/5 hover:border-[#2BA8FF]/60 hover:bg-white/10 transition-all active:scale-95">
              <ChevronLeft size={19} className="text-white" />
            </button>
            <div className="flex items-center gap-2">
              {t.examples.map((_, i) => (
                <button key={i} onClick={() => setExIdx(i)} aria-label={`slide ${i + 1}`}
                  className={`h-2 rounded-full transition-all ${i === exIdx ? "w-7 bg-[#2BA8FF]" : "w-2 bg-white/20 hover:bg-white/40"}`} />
              ))}
            </div>
            <button data-testid="examples-next" onClick={() => setExIdx((exIdx + 1) % EX_N)}
              className="grid place-items-center w-11 h-11 rounded-full border border-white/12 bg-white/5 hover:border-[#2BA8FF]/60 hover:bg-white/10 transition-all active:scale-95">
              <ChevronRight size={19} className="text-white" />
            </button>
          </div>

          {/* скрытые аудио (одни на все карточки) */}
          <div className="hidden">
            {t.examples.map((ex, i) => (
              <audio key={i} ref={(el) => (exRefs.current[i] = el)} src={ex.audio} preload="none"
                onTimeUpdate={(e) => setExTime(e.target.currentTime)}
                onLoadedMetadata={(e) => setExDur((d) => { const n = [...d]; n[i] = e.target.duration; return n; })}
                onEnded={() => { setExPlaying(null); setExTime(0); }} />
            ))}
          </div>
        </div>
      </section>

      {/* СТУДІЯ — только для UA-версии */}
      {!enSite && (
        <section className="px-5 pt-2 pb-8" data-testid="tariff-studio-section">
          <div className="max-w-xl lg:max-w-3xl mx-auto">
            <PlanCard plan={plans[2]} accent={ACCENTS[1]} testid="tariff-team"
              onClick={() => { track("tariff_team"); setPremiumOpen(true); }} />
          </div>
        </section>
      )}

      {/* pills — премиум-карточки */}
      <section className="px-5 pt-2 pb-2">
        <div className="max-w-5xl mx-auto grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {t.pills.map((p, i) => (
            <div key={i} className="relative overflow-hidden rounded-3xl border border-white/10 bg-[#0E1526] p-4 lift"
              style={{ boxShadow: "inset 0 1px 0 rgba(255,255,255,0.06)" }}>
              <div className="absolute inset-0 pointer-events-none"
                style={{ background: "radial-gradient(120% 100% at 18% 0%, rgba(43,168,255,0.14), transparent 62%)" }} />
              <div className="relative flex items-center gap-3.5">
                <span className="grid place-items-center w-11 h-11 rounded-2xl shrink-0 border border-white/10"
                  style={{ background: "radial-gradient(circle at 30% 25%, rgba(43,168,255,0.25), rgba(6,10,20,0.5))" }}>
                  <img src={PILL_ICONS[i]} alt="" className="w-6 h-6 object-contain" style={{ filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.45))" }} />
                </span>
                <span className="text-[13px] text-[#C6D3E6] leading-snug">{p.t}</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* SOCIAL PROOF */}
      <section className="px-5 pt-6 pb-2">
        <div className="max-w-2xl lg:max-w-5xl mx-auto grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[
            {
              v: `${nf(stats.songs_generated)}+`, l: t.sp_songs, Icon: Music4,
              grad: "linear-gradient(120deg,#2BA8FF,#7CC5FF)", glow: "rgba(43,168,255,0.22)",
              color: "#2BA8FF",
            },
            {
              v: `${stats.satisfaction}%`, l: t.sp_rating, Icon: Star,
              grad: "linear-gradient(120deg,#FFD400,#FFB300)", glow: "rgba(255,212,0,0.18)",
              color: "#FFD400",
            },
          ].map((s, i) => {
            const Icon = s.Icon;
            return (
              <div key={i} className="relative overflow-hidden rounded-3xl border border-white/10 bg-[#0E1526] p-5 sm:p-6 lift"
                style={{ boxShadow: "inset 0 1px 0 rgba(255,255,255,0.06)" }}>
                <div className="absolute inset-0 pointer-events-none"
                  style={{ background: `radial-gradient(120% 90% at 18% 0%, ${s.glow}, transparent 62%)` }} />
                <div className="relative flex items-center gap-4">
                  <span className="grid place-items-center w-12 h-12 rounded-2xl bg-white/5 border border-white/10 shrink-0">
                    <Icon size={22} style={{ color: s.color }} />
                  </span>
                  <div className="min-w-0">
                    <div className="text-2xl sm:text-3xl font-display font-extrabold leading-none"
                      style={{ background: s.grad, WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent" }}>
                      {s.v}
                    </div>
                    <div className="text-[10px] sm:text-xs text-[#8FA0BA] mt-1.5 font-mono uppercase tracking-wider">{s.l}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* HOW */}
      <section id="how" className="py-14 sm:py-20 px-5 max-w-5xl mx-auto">
        <SectionHead title={t.how_title} sub={t.how_sub} />
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4 mt-12">
          {t.how_steps.map((s, i) => {
            const Icon = STEP_ICONS[i];
            return (
              <div key={i} className="card-soft rounded-2xl p-5 lift relative">
                <span className="font-mono text-xs text-[#2BA8FF]">0{i + 1}</span>
                <div className="grid place-items-center w-11 h-11 rounded-xl bg-white/5 border border-white/10 my-3">
                  <Icon size={20} className="text-[#FFD400]" />
                </div>
                <h3 className="font-semibold text-[15px]">{s.t}</h3>
                <p className="text-sm text-[#647089] mt-1.5 leading-relaxed">{s.d}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* QUALITY GUARANTEES */}
      <section className="py-14 sm:py-20 px-5 max-w-5xl mx-auto">
        <SectionHead title={t.quality_title} sub={t.quality_sub} />
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-12">
          {t.quality.map((q, i) => {
            const Icon = QUALITY_ICONS[q.icon] || ShieldCheck;
            return (
              <div key={i} className="card-soft rounded-2xl p-6 lift flex flex-col">
                <span className="grid place-items-center w-12 h-12 rounded-2xl border border-white/10 mb-4"
                  style={{ background: "radial-gradient(circle at 30% 25%, rgba(43,168,255,0.22), rgba(6,10,20,0.5))" }}>
                  <Icon size={22} className="text-[#2BA8FF]" />
                </span>
                <h3 className="font-semibold text-[15px]">{q.t}</h3>
                <p className="text-sm text-[#647089] mt-2 leading-relaxed">{q.d}</p>
              </div>
            );
          })}
        </div>
        <div className="mt-8 rounded-3xl card-soft p-6 sm:p-8 text-center relative overflow-hidden">
          <div className="absolute inset-0 hero-flag pointer-events-none opacity-60" />
          <p className="relative font-display text-xl sm:text-2xl font-extrabold uppercase">
            <span className="text-yellow">💛</span> {t.quality_slogan}
          </p>
        </div>
      </section>

      {/* REFERRAL — только для UA-версии */}
      {!enSite && (
      <section className="py-14 sm:py-20 px-5 max-w-5xl mx-auto">
        <div className="relative rounded-3xl overflow-hidden card-soft p-8 sm:p-10 md:p-14">
          <div className="absolute inset-0 hero-flag" />
          <div className="relative flex flex-col md:flex-row items-center gap-8">
            <div className="grid place-items-center w-20 h-20 rounded-2xl grad-tg shrink-0 floaty"><Gift size={34} className="text-white" /></div>
            <div className="flex-1 text-center md:text-left">
              <h2 className="text-2xl sm:text-3xl font-display font-bold uppercase">{t.referral_title}</h2>
              <p className="text-[#9FB0C7] mt-3 max-w-2xl">{t.referral_sub}</p>
            </div>
            <button data-testid="referral-cta-button" onClick={() => goBot("referral")} className="bg-[#FFD400] text-[#060A14] text-sm font-semibold px-4 py-2.5 rounded-xl hover:opacity-90 transition-opacity whitespace-nowrap">{t.referral_cta}</button>
          </div>
        </div>
      </section>
      )}

      {/* FAQ */}
      <section className="py-14 sm:py-20 px-5 max-w-3xl mx-auto">
        <SectionHead title={t.faq_title} />
        <div className="mt-10 space-y-3">
          {t.faq.map((f, i) => (
            <div key={i} className="card-soft rounded-2xl overflow-hidden">
              <button data-testid={`faq-item-${i}`} onClick={() => setOpenFaq(openFaq === i ? -1 : i)} className="w-full flex items-center justify-between p-5 text-left">
                <span className="font-semibold text-[15px]">{f.q}</span>
                <ChevronDown size={18} className={`transition-transform ${openFaq === i ? "rotate-180 text-[#2BA8FF]" : "text-[#647089]"}`} />
              </button>
              {openFaq === i && <p className="px-5 pb-5 text-sm text-[#9FB0C7] leading-relaxed">{f.a}</p>}
            </div>
          ))}
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-14 sm:py-20 px-5 text-center relative">
        <div className="absolute inset-0 hero-flag pointer-events-none" />
        <div className="relative">
          <div className="flex justify-center mb-5"><LogoBars size={44} /></div>
          <h2 className="font-display text-3xl sm:text-4xl font-extrabold uppercase max-w-2xl mx-auto leading-tight">{t.hero_title_1} <span className="text-yellow">{t.hero_title_hl}</span></h2>
          <p className="hand text-2xl text-[#2BA8FF] mt-3">{t.tagline}</p>
          <button data-testid="footer-cta-button" onClick={() => openSite("footer")} className="btn-tg grad-tg text-white font-bold px-8 py-4 rounded-2xl mt-6 inline-flex items-center gap-2">
            <Send size={18} /> {t.hero_cta_song}
          </button>
        </div>
      </section>

      {!enSite && <PremiumModal open={premiumOpen} onClose={() => setPremiumOpen(false)} />}
      {enSite && <SupportModal open={supportOpen} onClose={() => setSupportOpen(false)} />}
      <MyOrdersModal open={myOrdersOpen} onClose={() => setMyOrdersOpen(false)} />

      {!enSite && instrOpen && createPortal(
        <div className="instr-wrap" role="dialog" aria-modal="true" data-testid="instruction-modal">
          <div className="instr-back" onClick={() => setInstrOpen(false)} />
          <div className="instr-stage">
            <button type="button" className="instr-close" onClick={() => setInstrOpen(false)} aria-label={t.done_close}>
              <X size={20} />
            </button>
            <video className="instr-video" data-testid="instruction-video"
              src="/design/video/site-instruction.mp4"
              controls autoPlay playsInline preload="metadata" />
          </div>
        </div>,
        document.body)}

      {!enSite && giftOpen && createPortal(
        <div className="gift-wrap" role="dialog" aria-modal="true" data-testid="gift-modal">
          <div className="gift-back" onClick={closeGift} />
          <div className="gift-modal">
            <button type="button" className="gift-close" onClick={closeGift} aria-label={t.done_close}>
              <X size={18} />
            </button>
            <div className="gift-ico">🎁</div>
            <div className="gift-title">Вам доступна знижка!</div>
            <p className="gift-text">
              Пісня всього за <b className="gift-price">169 грн</b>, при оформленні
              через наш додаток в телеграм!
            </p>
            <button type="button" data-testid="gift-activate" className="gift-cta"
              onClick={() => { track("gift_activate"); closeGift(); }}>
              Активувати
            </button>
          </div>
        </div>,
        document.body,
      )}

      <footer className="border-t border-white/5 py-8 px-5 text-center text-sm text-[#647089]">
        <div className="flex items-center justify-center gap-2 font-display font-bold mb-2 text-white"><LogoBars size={20} /> E-PISNYA</div>
        <p>{t.footer}</p>
        <p className="hand text-lg text-[#FFD400] mt-1">{t.footer_note}</p>
      </footer>

      {/* Конструктор и визард — без кнопки на странице */}
      <CreateSongCta goBot={goBot} bare />

      {/* Виджет «Ваше останнє створення»: черновик/неоплаченный заказ */}
      <LastOrderWidget />
    </div>
  );
}

const SectionHead = ({ title, sub }) => (
  <div className="text-center max-w-2xl mx-auto">
    <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold uppercase tracking-tight">{title}</h2>
    {sub && <p className="text-[#9FB0C7] mt-3">{sub}</p>}
  </div>
);
